import React, { useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import UFOLabel from '@atlaskit/react-ufo/label';

import { CategorySelector } from '@confluence/category-selector';
import { DebouncedSearchBar } from '@confluence/debounced-search-bar';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ANALYTICS_ACTION,
	ANALYTICS_SOURCE,
	ANALYTICS_ORIGIN,
	ANALYTICS_SUBJECT,
	ANALYTICS_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import {
	SNIPPET_CATEGORY_IDS,
	useSnippetFilterCategories,
} from '../constants/snippetCategories.ts';
import { useSnippets } from '../store/snippetsStore.ts';
import { filterSnippets, useSnippetFilterAndSort } from '../hooks/useSnippetFilterAndSort.ts';
import { useIsAtlassianStaff } from '../hooks/useIsAtlassianStaff.ts';

import { EmptySnippets, EMPTY_STATE_VARIANT } from '../empty-state/EmptySnippets.tsx';
import { getEmptyStateVariant } from '../utils/emptyStateVariant.ts';

import { SnippetPanelList } from './SnippetPanelList.tsx';
import { SnippetPanelSkeleton } from './skeleton/SnippetPanelSkeleton.tsx';
import { SnippetPopupPreviewProvider } from './SnippetPopupPreviewContext.tsx';
import { SnippetSortDropdown } from './SnippetSortDropdown.tsx';

const SLACK_FEEDBACK_URL = 'https://atlassian.enterprise.slack.com/archives/C0A819RF2SV';

const messages = defineMessages({
	snippetsRecommended: {
		id: 'snippets.panel.recommended',
		defaultMessage: 'Recommended',
		description: 'Header for the list of Block Templates within the right panel',
	},
	searchSnippetsPlaceholderText: {
		id: 'snippets.panel.search.placeholderText',
		defaultMessage: 'Search author, category, topic',
		description: 'The placeholder text in the templates search box, visible when it is empty',
	},
	feedbackButtonLabel: {
		id: 'snippets.panel.feedback.button',
		defaultMessage: 'Give feedback',
		description: 'Label for the link to the Block Templates feedback Slack channel',
	},
});

const styles = cssMap({
	feedbackBar: {
		paddingBlock: token('space.200'),
		paddingInline: token('space.0'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface'),
	},
});

type SnippetPanelProps = {
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanel = ({ contentId, spaceId, spaceKey }: SnippetPanelProps) => {
	const { userId } = useSessionData();
	const intl = useIntl();
	const isInternalUser = useIsAtlassianStaff();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isLoading, snippets }] = useSnippets();
	const previousCategoryRef = useRef<string | null>(null);

	const {
		selectedCategoryId,
		searchQuery,
		sortOrder,
		filteredSnippets,
		setSearchQuery,
		setCategoryFilter,
		setSortOrder,
	} = useSnippetFilterAndSort(snippets, userId);
	const categories = useSnippetFilterCategories();

	const handleCategorySelected = (category: { id: string }) => {
		const previousCategoryId = previousCategoryRef.current;
		previousCategoryRef.current = category.id;

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: ANALYTICS_SOURCE,
				actionSubject: ANALYTICS_SUBJECT.FILTER,
				action: ANALYTICS_ACTION.CLICKED,
				actionSubjectId: ANALYTICS_SUBJECT_ID.CATEGORY,
				type: 'ui',
				attributes: {
					origin: ANALYTICS_ORIGIN.SNIPPET_PANEL,
					selectedCategoryId: category.id,
					...(previousCategoryId != null && { previousCategoryId }),
				},
			},
		}).fire();

		setCategoryFilter(category.id as SNIPPET_CATEGORY_IDS);
	};

	const handleSearch = (query: string) => {
		if (query.length > 0) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: ANALYTICS_SOURCE,
					actionSubject: ANALYTICS_SUBJECT.FILTER,
					action: ANALYTICS_ACTION.SEARCHED,
					actionSubjectId: ANALYTICS_SUBJECT_ID.SEARCH,
					type: 'ui',
					attributes: {
						origin: ANALYTICS_ORIGIN.SNIPPET_PANEL,
						searchQueryLength: query.length,
					},
				},
			}).fire();
		}
		setSearchQuery(query);
	};

	const snippetsInCategoryOnly = filterSnippets(
		snippets,
		selectedCategoryId,
		'',
		userId ?? undefined,
	);
	const isCategoryEmpty = snippetsInCategoryOnly.length === 0;
	const emptyStateVariant = getEmptyStateVariant(isCategoryEmpty, selectedCategoryId);

	if (isLoading) {
		return <SnippetPanelSkeleton />;
	}

	return (
		<Stack space="space.200" grow="fill">
			<DebouncedSearchBar
				placeholder={intl.formatMessage(messages.searchSnippetsPlaceholderText)}
				isCompact
				onChange={handleSearch}
				initialValue={searchQuery || ''}
			/>
			<CategorySelector
				categories={categories}
				selectedCategoryIds={[selectedCategoryId]}
				onCategorySelected={handleCategorySelected}
			/>
			<SnippetSortDropdown onChange={setSortOrder} value={sortOrder} />
			{filteredSnippets.length === 0 ? (
				<EmptySnippets
					variant={emptyStateVariant}
					primaryAction={
						emptyStateVariant === EMPTY_STATE_VARIANT.FAVORITES
							? () => setCategoryFilter(SNIPPET_CATEGORY_IDS.ALL)
							: undefined
					}
				/>
			) : (
				<SnippetPopupPreviewProvider>
					<UFOLabel name="snippet-panel">
						<SnippetPanelList
							snippets={filteredSnippets}
							contentId={contentId}
							spaceId={spaceId}
							spaceKey={spaceKey}
						/>
					</UFOLabel>
				</SnippetPopupPreviewProvider>
			)}
			{isInternalUser && (
				<Box xcss={styles.feedbackBar}>
					<LinkButton
						href={SLACK_FEEDBACK_URL}
						iconBefore={FeedbackIcon}
						target="_blank"
						rel="noopener noreferrer"
						appearance="subtle"
					>
						<FormattedMessage {...messages.feedbackButtonLabel} />
					</LinkButton>
				</Box>
			)}
		</Stack>
	);
};
